import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { reduxForm, Field, change, formValueSelector } from 'redux-form';
import i18n from '../../../i18n';
import { Grid } from '@material-ui/core';
import { number, string } from 'yup';

import { appConfig } from 'config/app';

import { useFormError, validator } from 'core/form/form';

import { isUIEnabledSelector } from 'state/app/selectors';
import { deliveryServiceOptionsSelector } from 'state/deliveryService/selector';
import { mapDeliveryServiceOptionsToFormData } from 'state/deliveryService/utils';

import { FormErrorMessage } from 'components/FormErrorMessage/FormErrorMessage';
import { LargeButton } from 'components/LargeButton/LargeButton';
import { NumberControlField } from 'components/NumberControl/NumberControl';
import { TextControlField } from 'components/TextControl/TextControl';

import { Form } from '../types';
import { FormAddress } from 'components/FormAddress/FormAddress';
import { useAction } from 'core/store/store';

const formSelector = formValueSelector(Form.DELIVERY_DETAILS_FORM);
const senderAddressCoordinatesSelector = <T extends {}>(state: T) => formSelector(state, 'senderAddressCoordinates');
const recipientAddressCoordinatesSelector = <T extends {}>(state: T) =>
    formSelector(state, 'recipientAddressCoordinates');

export const DeliveryDetailsForm = reduxForm({
    form: Form.DELIVERY_DETAILS_FORM,
    initialValues: {
        weight: null,
        senderAddress: '',
        senderPhone: '',
        senderEmail: '',
        recipientAddress: '',
        recipientPhone: '',
        recipientName: '',
        width: null,
        height: null,
        length: null,
    },
    validate: validator({
        weight: number().nullable().required('common:validation.required'),
        senderAddress: string().required('common:validation.required'),
        senderPhone: string().required('common:validation.required'),
        email: string().email('common:validation.emailFormat').required('common:validation.required'),
        recipientAddress: string().required('common:validation.required'),
        recipientPhone: string().required('common:validation.required'),
        recipientName: string().required('common:validation.required'),
        width: number().nullable().required('common:validation.required'),
        height: number().nullable().required('common:validation.required'),
        length: number().nullable().required('common:validation.required'),
    }),
})(props => {
    const { t } = i18n.useTranslation();
    const action = useAction();
    const { form, handleSubmit } = props;
    const error = useFormError(Form.DELIVERY_DETAILS_FORM);
    console.log('error', error);
    const isUIEnabled = useSelector(isUIEnabledSelector);
    const orderDeliveryServiceOptions = useSelector(deliveryServiceOptionsSelector);
    const senderAddressCoordinates = useSelector(senderAddressCoordinatesSelector);
    const recipientAddressCoordinates = useSelector(recipientAddressCoordinatesSelector);
    useEffect(() => {
        if (orderDeliveryServiceOptions) {
            const formData = mapDeliveryServiceOptionsToFormData(orderDeliveryServiceOptions);
            Object.entries(formData).forEach(entry => {
                props.change(entry[0], entry[1]);
            });
        }
    }, [orderDeliveryServiceOptions]);

    const handleSenderAddressCoordinatesChange = useCallback(
        (coordinates: { lat: number; lon: number }) => {
            action(change, form, 'senderAddressCoordinates', coordinates);
        },
        [action, form],
    );

    const handleRecipientAddressCoordinatesChange = useCallback(
        (coordinates: { lat: number; lon: number }) => {
            action(change, form, 'recipientAddressCoordinates', coordinates);
        },
        [action, form],
    );

    return (
        <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Field
                        name="senderAddress"
                        component={FormAddress}
                        label={t('orders:deliveryCard.senderAddress')}
                        initialCoordinates={appConfig.defaultCoordinates}
                        onCoordinatesChange={handleSenderAddressCoordinatesChange}
                        coordinates={senderAddressCoordinates}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Field
                        name="senderPhone"
                        component={TextControlField}
                        label={t('orders:deliveryCard.senderPhone')}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Field
                        name="senderEmail"
                        component={TextControlField}
                        defaultValue={'tessa'}
                        label={t('orders:deliveryCard.senderEmail')}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Field
                        name="recipientAddress"
                        component={FormAddress}
                        label={t('orders:deliveryCard.recipientAddress')}
                        initialCoordinates={appConfig.defaultCoordinates}
                        onCoordinatesChange={handleRecipientAddressCoordinatesChange}
                        coordinates={recipientAddressCoordinates}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Field
                        name="recipientPhone"
                        component={TextControlField}
                        label={t('orders:deliveryCard.recipientPhone')}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Field
                        name="recipientName"
                        component={TextControlField}
                        label={t('orders:deliveryCard.recipientName')}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Field
                        name="width"
                        component={NumberControlField}
                        label={t('orders:deliveryCard.width')}
                        step={0.001}
                        min={0}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Field
                        name="height"
                        component={NumberControlField}
                        label={t('orders:deliveryCard.height')}
                        step={0.001}
                        min={0}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Field
                        name="length"
                        component={NumberControlField}
                        label={t('orders:deliveryCard.length')}
                        step={0.001}
                        min={0}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Field
                        name="weight"
                        component={NumberControlField}
                        label={t('orders:deliveryCard.weight')}
                        step={0.001}
                        min={0}
                    />
                </Grid>
                {error && (
                    <Grid item xs={12}>
                        <FormErrorMessage error={error} />
                    </Grid>
                )}
                <Grid item xs={12}>
                    <LargeButton disabled={!isUIEnabled} type="submit">
                        {t('orders:deliveryCard.submitButton')}
                    </LargeButton>
                </Grid>
            </Grid>
        </form>
    );
});
